
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";
import { canUserCopyPrompt, canUserSendCopilotMessage } from "@/services/subscriptionService";
import { Crown, Settings, AlertCircle, ExternalLink } from "lucide-react";
import PromptUsageMeter from "@/components/prompts/PromptUsageMeter";
import CopilotUsageMeter from "@/components/prompts/CopilotUsageMeter";
import PromptUpgradeDialog from "@/components/prompts/PromptUpgradeDialog";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";

const SubscriptionStatus = () => {
  const { subscribed, subscription_tier, subscription_end, loading, openCustomerPortal, checkSubscription } = useSubscription();
  const { user } = useAuth();
  const [usageInfo, setUsageInfo] = useState<{
    canCopy: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null>(null);
  const [copilotUsageInfo, setCopilotUsageInfo] = useState<{
    canSend: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null>(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchUsageInfo = async () => {
      if (user) {
        try {
          const [promptUsage, copilotUsage] = await Promise.all([
            canUserCopyPrompt(user.id),
            canUserSendCopilotMessage(user.id)
          ]);
          setUsageInfo(promptUsage);
          setCopilotUsageInfo(copilotUsage);
        } catch (error) {
          console.error('Error fetching usage info:', error);
        }
      }
    };

    fetchUsageInfo();
  }, [user, subscription_tier]); // Re-fetch when subscription changes

  const handleUpgrade = () => {
    setUpgradeDialogOpen(true);
  };

  const handleUpgradeFromDialog = async (tierId: string) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      // Get the current billing period from the upgrade dialog
      // We need to check if there's a way to get this from the dialog state
      // For now, we'll default to monthly, but this needs to be passed from the dialog
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          tierId, 
          billingPeriod: 'monthly' // This is the issue - it should come from the dialog
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
        setUpgradeDialogOpen(false);
        
        // Set up listener for when user returns from Stripe
        const handleVisibilityChange = async () => {
          if (document.visibilityState === 'visible') {
            // Wait a moment for Stripe webhook to process
            setTimeout(async () => {
              await checkSubscription();
              // Refresh usage info
              if (user) {
                const [promptUsage, copilotUsage] = await Promise.all([
                  canUserCopyPrompt(user.id),
                  canUserSendCopilotMessage(user.id)
                ]);
                setUsageInfo(promptUsage);
                setCopilotUsageInfo(copilotUsage);
              }
            }, 2000);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout process. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Subscription Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayTier = subscription_tier ? 
    subscription_tier.charAt(0).toUpperCase() + subscription_tier.slice(1) : 
    "Free";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Subscription Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Current Plan:</span>
            <Badge variant={subscribed ? "default" : "secondary"}>
              {displayTier}
            </Badge>
          </div>
          
          {usageInfo && (
            <div className="space-y-2">
              <PromptUsageMeter 
                usedCount={usageInfo.usedCount}
                limit={usageInfo.limit}
                percentUsed={usageInfo.percentUsed}
              />
            </div>
          )}

          {copilotUsageInfo && (
            <div className="space-y-2">
              <CopilotUsageMeter 
                usedCount={copilotUsageInfo.usedCount}
                limit={copilotUsageInfo.limit}
                percentUsed={copilotUsageInfo.percentUsed}
              />
            </div>
          )}
          
          {subscribed && subscription_end && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Renews:</span>
              <span className="text-sm text-muted-foreground">
                {new Date(subscription_end).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Show helpful info for free users */}
          {!subscribed && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Upgrade to unlock unlimited prompts, priority support, and exclusive features.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="flex gap-2">
            {subscribed && (
              <Button
                variant="outline"
                size="sm"
                onClick={openCustomerPortal}
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Manage Subscription
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
            
            {!subscribed && (
              <Button
                size="sm"
                onClick={handleUpgrade}
                className="flex items-center gap-2"
              >
                <Crown className="h-4 w-4" />
                Upgrade Plan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <PromptUpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        usageInfo={usageInfo}
        onUpgrade={(tierId, billingPeriod) => {
          // Now we properly receive the billing period from the dialog
          if (isProcessing) return;
          
          setIsProcessing(true);
          
          supabase.functions.invoke('create-checkout', {
            body: { 
              tierId, 
              billingPeriod, // Use the correct billing period from the dialog
              isUpgrade: true
            }
          }).then(({ data, error }) => {
            if (error) throw error;
            
            if (data?.url) {
              window.open(data.url, '_blank');
              setUpgradeDialogOpen(false);
              
              // Set up listener for when user returns from Stripe
              const handleVisibilityChange = async () => {
                if (document.visibilityState === 'visible') {
                  setTimeout(async () => {
                    await checkSubscription();
                    if (user) {
                      const [promptUsage, copilotUsage] = await Promise.all([
                        canUserCopyPrompt(user.id),
                        canUserSendCopilotMessage(user.id)
                      ]);
                      setUsageInfo(promptUsage);
                      setCopilotUsageInfo(copilotUsage);
                    }
                  }, 2000);
                  document.removeEventListener('visibilitychange', handleVisibilityChange);
                }
              };
              
              document.addEventListener('visibilitychange', handleVisibilityChange);
            } else {
              throw new Error('No checkout URL returned');
            }
          }).catch(error => {
            console.error('Checkout error:', error);
            toast.error('Failed to start checkout process. Please try again.');
          }).finally(() => {
            setIsProcessing(false);
          });
        }}
      />
    </>
  );
};

export default SubscriptionStatus;
